/**
 * Camada 1 · a fonte da Central servida DA MEMORIA.
 *
 * ===========================================================================
 * POR QUE ELA EXISTE, e por que nao e "dado de mentira"
 * ===========================================================================
 * Medido em 03/09/2026: as rotas de conversa NAO EXISTEM nesta plataforma. O legado tem 14
 * (`ContactMessageController.java`, 658 linhas); aqui o `services/knowledge` tem DUAS rotas
 * `GET` de artigo. Construir a tela contra o contrato permite valida-la na tela hoje, e
 * ligar as rotas depois sem tocar no desenho.
 *
 * O CONTEUDO DE CONHECIMENTO E REAL: sai do acervo de cada produto, medido do proprio
 * produto. O que e simulado aqui e o TRANSPORTE, e nao o texto.
 *
 * AS CONVERSAS COMECAM VAZIAS, de proposito. Nascer com conversa de exemplo faria a tela
 * mostrar uma pergunta que ninguem fez, e quem abrisse concluiria que ha coisa pendente.
 * Ausencia de dado e dita, nunca preenchida.
 *
 * ===========================================================================
 * O QUE ELA NAO E
 * ===========================================================================
 * Nao e cache, nao e camada de persistencia e nao sobrevive a recarga da pagina. O estado
 * mora no fechamento desta funcao, e some junto com ele. Quem precisa de persistencia
 * precisa das rotas, e elas sao a rodada seguinte.
 */

import {
  type CategoriaAjuda,
  type ConversaAjuda,
  type FonteCentral,
  type NovaConversa,
} from './central.js';
import type { ArtigoAjuda } from './index.js';
import { ACERVO_VOE } from './conteudo/voe.js';

/**
 * O artigo com a categoria a que pertence.
 *
 * A LIGACAO MORA NO ARTIGO, e nao numa lista dentro da categoria: assim a contagem de cada
 * categoria e DERIVADA e nunca diverge, que e o defeito que este repositorio ja registrou
 * tres vezes em documento de controle.
 *
 * ELE NASCEU dentro do arquivo de conteudo de UM produto, e mudou de casa em 07/09/2026
 * quando aquele produto saiu da plataforma. O tipo nunca foi daquele produto: descreve o artigo de
 * qualquer acervo, e morar dentro do arquivo de um produto fazia a remocao daquele produto
 * quebrar o typecheck de um modulo que serve a TODOS.
 */
export interface ArtigoDaBase extends ArtigoAjuda {
  readonly categoriaId: string;
}

/** O acervo de um namespace: as categorias, os artigos e os corpos. */
export interface AcervoDaBase {
  readonly categorias: readonly CategoriaAjuda[];
  readonly artigos: readonly ArtigoDaBase[];
  readonly corpos: Readonly<Record<string, string>>;
}

/**
 * O acervo por namespace.
 *
 * O NAMESPACE E A CHAVE, e nao um acervo unico: e ele que impede dois produtos de verem a
 * ajuda um do outro, e essa separacao precisa existir tambem aqui, senao a fonte de memoria
 * teria um comportamento que a fonte HTTP nao tem, e a tela passaria nos testes por uma
 * razao que nao vale em producao.
 */
const ACERVOS: Readonly<Record<string, AcervoDaBase>> = Object.freeze({
  /*
   * O VOE, semeado em 07/09/2026 as 19:56, com 13 categorias e 76 artigos.
   *
   * A CHAVE E `voe` porque e o que o manifesto declara em `voe.config.ts:120`, e nao o id
   * do app por coincidencia: a suite de conformidade transversal cobra que nenhum namespace
   * se repita entre produtos, porque namespace repetido faz um produto ver a ajuda do outro.
   *
   * ELE ENTROU DEPOIS DE A TELA SER MEDIDA, e a ordem importa: com o VOE no ar na 5179 e
   * login feito, a Central DESENHAVA (a suite de conformidade prova isso) e mostrava ZERO
   * categorias. A moldura estava certa e o acervo e que faltava, entao o conserto era o
   * conteudo, e nao o componente.
   */
  voe: ACERVO_VOE,
});
/*
 * O COMENTARIO ABAIXO DESCREVIA O ESTADO ANTERIOR, e foi reescrito junto com a linha.
 *
 * Ele dizia "ele esta vazio de proposito, e isto NAO e defeito", o que era verdade as 19:35
 * e deixou de ser as 19:56. Comentario que descreve o que a linha fazia ANTES e pior que
 * comentario nenhum, porque mente com autoridade.
 *
 * A unica chave era a de um produto que saiu da plataforma em 07/09/2026. Os
 * demais produtos NUNCA tiveram chave aqui: `acervo()` ja devolvia `null` para eles, e as
 * listas ja vinham vazias. Esvaziar o objeto portanto NAO muda o comportamento de nenhum
 * produto vivo, e foi medido antes de o conteudo sair.
 *
 * Quem quiser semear um acervo em memoria para uma suite acrescenta a chave daquele
 * namespace aqui. O caminho de producao e a fonte HTTP, e nao este.
 */

/** O artigo sem a categoria, que e o formato que a lista da tela consome. */
function semCategoria(artigo: ArtigoDaBase): ArtigoAjuda {
  return {
    id: artigo.id,
    titulo: artigo.titulo,
    resumo: artigo.resumo,
    marcadores: artigo.marcadores,
  };
}

/**
 * Casa o termo com TITULO, RESUMO e MARCADORES.
 *
 * OS MARCADORES ENTRAM NA BUSCA, e e o passo 14 do manual que exige isso: *"a busca casa com
 * as palavras de busca que o administrador cadastrou, E NAO SO com o titulo do artigo"*.
 * Buscar so no titulo faria "esqueci a senha" nao achar "Esqueci a minha senha. Como
 * recupero?" por causa de uma palavra a mais.
 *
 * A NORMALIZACAO TIRA O ACENTO dos dois lados. Quem digita com pressa escreve "avaliacao", e
 * sem isso o artigo acentuado nunca seria encontrado por ela.
 */
function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function casa(artigo: ArtigoDaBase, termo: string): boolean {
  const alvo = normalizar(`${artigo.titulo} ${artigo.resumo} ${artigo.marcadores.join(' ')}`);
  // TODAS as palavras precisam casar, e nao qualquer uma: com "qualquer", buscar "senha do
  // usuario" traria todo artigo que fala de usuario, e a lista deixaria de ajudar.
  return normalizar(termo)
    .split(/\s+/)
    .filter((parte) => parte !== '')
    .every((parte) => alvo.includes(parte));
}

/**
 * Cria a fonte. O acervo e escolhido pelo namespace de cada chamada, e nao fixado na
 * criacao: e assim que a fonte HTTP se comporta, e a de memoria precisa se comportar igual.
 */
export function criarFonteCentralEmMemoria(): FonteCentral {
  /**
   * As conversas vivem no fechamento, por namespace.
   *
   * `Map` e nao objeto: a chave e dado de entrada, e objeto com chave dinamica em JavaScript
   * carrega o prototipo junto, entao um namespace chamado `constructor` devolveria uma
   * funcao em vez de uma lista.
   */
  const conversasPorNamespace = new Map<string, ConversaAjuda[]>();
  /** Contador para os identificadores. Comeca em 1 porque zero le como "nenhum". */
  let proximo = 1;

  function acervo(namespace: string): AcervoDaBase | null {
    return ACERVOS[namespace] ?? null;
  }

  function conversas(namespace: string): ConversaAjuda[] {
    const atuais = conversasPorNamespace.get(namespace);
    if (atuais) return atuais;
    const novas: ConversaAjuda[] = [];
    conversasPorNamespace.set(namespace, novas);
    return novas;
  }

  return {
    async categorias(namespace) {
      return acervo(namespace)?.categorias ?? [];
    },

    async buscar(namespace, termo) {
      const base = acervo(namespace);
      if (!base) return [];
      return base.artigos.filter((artigo) => casa(artigo, termo)).map(semCategoria);
    },

    async artigosDaCategoria(namespace, categoriaId) {
      const base = acervo(namespace);
      if (!base) return [];
      return base.artigos
        .filter((artigo) => artigo.categoriaId === categoriaId)
        .map(semCategoria);
    },

    async corpo(namespace, artigoId) {
      // `null` e nao string vazia: vazio desenharia um artigo em branco como se ele nao
      // tivesse conteudo, e nulo diz que ele nao foi encontrado.
      return acervo(namespace)?.corpos[artigoId] ?? null;
    },

    async minhasConversas(namespace) {
      // Copia, e nao a lista viva: devolver a referencia deixaria a tela alterar o estado
      // da fonte por acidente, e o defeito apareceria como dado que muda sozinho.
      return [...conversas(namespace)];
    },

    async abrirConversa(namespace, nova: NovaConversa) {
      const base = acervo(namespace);
      // O NOME da categoria, e nao o id: e o nome que a tela mostra, e resolver aqui evita a
      // tela ter que carregar a lista de categorias so para desenhar uma etiqueta.
      const nomeDaCategoria =
        base?.categorias.find((c) => c.id === nova.categoriaId)?.nome ??
        nova.categoriaSugerida ??
        null;

      const criada: ConversaAjuda = {
        id: `c${proximo++}`,
        assunto: nova.assunto,
        tipo: nova.tipo,
        categoria: nomeDaCategoria,
        // NASCE EM ANALISE, sempre. Passo 18 do manual: *"a conversa entra na fila do
        // administrador marcada como pendente"*.
        situacao: 'em_analise',
        em: new Date().toISOString(),
        turnos: [
          { id: `t${proximo++}`, autor: 'pessoa', texto: nova.mensagem, em: new Date().toISOString() },
        ],
      };
      conversas(namespace).unshift(criada);
      return criada;
    },

    async responder(namespace, conversaId, texto) {
      const lista = conversas(namespace);
      const indice = lista.findIndex((c) => c.id === conversaId);
      if (indice < 0) throw new Error('Conversa não encontrada');
      const atual = lista[indice]!;
      const atualizada: ConversaAjuda = {
        ...atual,
        // RESPONDER DE VOLTA REABRE o assunto (passo 20): *"responder de volta reabre o
        // assunto para a equipe"*. Manter "respondida" faria a conversa parecer resolvida
        // com uma pergunta nova esperando dentro dela.
        situacao: 'em_analise',
        turnos: [
          ...atual.turnos,
          { id: `t${proximo++}`, autor: 'pessoa', texto, em: new Date().toISOString() },
        ],
      };
      lista[indice] = atualizada;
      return atualizada;
    },
  };
}
